import authService from './authService';
import { ApiResponse } from '../types/common';
import {
  BankIntegration,
  BankIntegrationFormData,
  BankStatementDecisionItem,
  BankStatementDecisionResult,
  BankStatementPreview,
  BankStatementSupplierCandidates,
} from '../types/bankIntegration';
import { CertificateLogEntry, CertificateResult } from '../types/certificate';

/**
 * Serviço da integração bancária por conta (API de Extrato do Itaú).
 * Endpoints: /financial/checking-accounts/:id/bank-integration[/certificate]
 * e /financial/checking-accounts/:id/bank-statement/{preview,decisions}.
 */
class BankIntegrationService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  private async throwApiError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
      const body = await response.json();
      // Erro de validação chega como { message: "Validation failed", details: { campo: msg } }:
      // a mensagem sozinha não diz nada — os campos dizem.
      const details =
        body?.details && typeof body.details === 'object' && !Array.isArray(body.details)
          ? Object.entries(body.details as Record<string, unknown>)
              .filter(([, v]) => typeof v === 'string')
              .map(([k, v]) => `${k}: ${v}`)
          : [];
      message = details.length > 0 ? `${fallback} — ${details.join('; ')}` : body?.message || body?.error || fallback;
    } catch {
      /* corpo não-JSON — mantém fallback */
    }
    throw new Error(message);
  }

  /** GET .../bank-integration — null quando a conta ainda não tem integração. */
  async getIntegration(accountId: number): Promise<BankIntegration | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/checking-accounts/${accountId}/bank-integration`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar a integração bancária');
    const data: ApiResponse<BankIntegration | null> = await response.json();
    return data.data ?? null;
  }

  /** PUT .../bank-integration */
  async saveIntegration(accountId: number, payload: BankIntegrationFormData): Promise<BankIntegration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/checking-accounts/${accountId}/bank-integration`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao salvar a integração bancária');
    const data: ApiResponse<BankIntegration> = await response.json();
    return data.data!;
  }

  /**
   * POST .../bank-integration/certificate (multipart file + password).
   * O apiClient força Content-Type JSON, então o multipart vai por fetch com
   * fallback cookie → token (mesmo padrão de financialService.postMultipart).
   */
  async uploadCertificate(accountId: number, file: File, password: string): Promise<CertificateResult> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('file', file);
    fd.append('password', password);

    const url = `${baseUrl}/financial/checking-accounts/${accountId}/bank-integration/certificate`;
    const doFetch = async (token?: string | null) =>
      fetch(url, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) await this.throwApiError(response, 'Falha ao enviar o certificado');
    const data: ApiResponse<CertificateResult> = await response.json();
    return data.data!;
  }

  /**
   * POST .../bank-integration/certificate/test
   *
   * O backend responde 200 mesmo quando o teste é negativo — o corpo carrega o
   * resultado da checagem, que é uma resposta e não uma falha da requisição.
   */
  async testCertificate(accountId: number): Promise<CertificateResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/financial/checking-accounts/${accountId}/bank-integration/certificate/test`,
      {},
    );
    if (!response.ok) await this.throwApiError(response, "Falha ao testar o certificado");
    const data: ApiResponse<CertificateResult> = await response.json();
    return data.data!;
  }

  /** POST .../bank-integration/certificate/renew — fala com o STS do Itaú. */
  async renewCertificate(accountId: number): Promise<CertificateResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/financial/checking-accounts/${accountId}/bank-integration/certificate/renew`,
      {},
    );
    if (!response.ok) await this.throwApiError(response, "Falha ao renovar o certificado");
    const data: ApiResponse<CertificateResult> = await response.json();
    return data.data!;
  }

  /** GET .../bank-integration/certificate/logs */
  async getCertificateLogs(accountId: number, limit = 200): Promise<CertificateLogEntry[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/financial/checking-accounts/${accountId}/bank-integration/certificate/logs?limit=${limit}`,
    );
    if (!response.ok) await this.throwApiError(response, "Falha ao carregar o histórico do certificado");
    const data: ApiResponse<CertificateLogEntry[]> = await response.json();
    return data.data ?? [];
  }

  /** POST .../bank-statement/preview */
  async previewStatement(accountId: number, startDate: string, endDate: string): Promise<BankStatementPreview> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/financial/checking-accounts/${accountId}/bank-statement/preview`, {
      startDate,
      endDate,
    });
    if (!response.ok) await this.throwApiError(response, 'Falha ao consultar o extrato no banco');
    const data: ApiResponse<BankStatementPreview> = await response.json();
    return data.data!;
  }

  /**
   * GET .../bank-statement/supplier-candidates — lançamentos existentes e
   * parcelas em aberto de um fornecedor para explicar um débito, com as
   * combinações que fecham o valor.
   */
  async getSupplierCandidates(
    accountId: number,
    providerId: number,
    date: string,
    amountCents: number,
  ): Promise<BankStatementSupplierCandidates> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({ providerId: String(providerId), date, amountCents: String(amountCents) });
    const response = await apiClient.get(
      `/financial/checking-accounts/${accountId}/bank-statement/supplier-candidates?${params}`,
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar os candidatos do fornecedor');
    const data: ApiResponse<BankStatementSupplierCandidates> = await response.json();
    return data.data!;
  }

  /** POST .../bank-statement/decisions */
  async sendDecisions(accountId: number, items: BankStatementDecisionItem[]): Promise<BankStatementDecisionResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/financial/checking-accounts/${accountId}/bank-statement/decisions`, {
      items,
    });
    if (!response.ok) await this.throwApiError(response, 'Falha ao aplicar as decisões');
    const data: ApiResponse<BankStatementDecisionResult> = await response.json();
    return data.data!;
  }

  /**
   * DELETE .../bank-statement/decisions/:providerEventId — desfaz uma decisão
   * sem lançamento ('Já conciliado'/'Divergência'); o evento volta a "novo".
   */
  async undoDecision(accountId: number, providerEventId: string): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(
      `/financial/checking-accounts/${accountId}/bank-statement/decisions/${encodeURIComponent(providerEventId)}`,
    );
    if (!response.ok) await this.throwApiError(response, 'Falha ao desfazer a decisão');
  }
}

export const bankIntegrationService = new BankIntegrationService();
export default bankIntegrationService;
